import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { apiClient, ApiError } from '@/lib/api'
import { downloadFile } from '@/lib/reports'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge, Modal } from '@/components/ui/primitives'

export interface ArchiveRunSummary {
  id: number
  status: string
  boundaryDate: string
  totalBytes: number
  startedAtUtc: string
  completedAtUtc: string | null
  message: string | null
  files: string[]
}

export interface ArchivePreview {
  tenantCode: string
  retentionYears: number
  /** The furthest back a run may go today, or null when nothing is old enough. */
  boundary: string | null
  earliestData: string | null
  counts: { table: string; rows: number }[]
  ineligible: string[]
  daysMissingASummary: string[]
  lastRun: ArchiveRunSummary | null
  totalRows: number
  canExport: boolean
}

/** Bytes as something a person reads — an archive is measured in hundreds of megabytes. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

/**
 * Writing one store's old detail out to a file, downloading it, and — only then — deleting it.
 *
 * The delete stays shut until a verified export exists for that exact cut-off, and asks for the
 * store code to be typed. Every other confirmation in this panel is a single click; this is the one
 * that cannot be undone, and the operator can be on the wrong row.
 */
export function ArchiveDialog({
  tenantId,
  storeName,
  onClose,
}: {
  tenantId: number | null
  storeName: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  /** Typed store code. The gate on the only irreversible thing in this panel. */
  const [confirmCode, setConfirmCode] = useState('')

  useEffect(() => {
    if (tenantId !== null) {
      setError(null)
      setConfirmCode('')
    }
  }, [tenantId])

  const preview = useQuery({
    queryKey: ['platform', 'archive-preview', tenantId],
    queryFn: () => apiClient.get<ArchivePreview>(`/api/platform/tenants/${tenantId}/archive/preview`),
    enabled: tenantId !== null,
  })

  const write = useMutation({
    mutationFn: () =>
      apiClient.post<ArchiveRunSummary>(`/api/platform/tenants/${tenantId}/archive/export`, {}),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'archive-preview', tenantId] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? (caught.detail ?? caught.message)
          : 'Could not write the archive.',
      ),
  })

  const prune = useMutation({
    mutationFn: (runId: number) =>
      apiClient.post<ArchiveRunSummary>(`/api/platform/tenants/${tenantId}/archive/prune`, {
        runId,
        confirmCode,
      }),
    onSuccess: async () => {
      setConfirmCode('')
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['platform', 'archive-preview', tenantId] })
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError ? (caught.detail ?? caught.message) : 'Could not prune.',
      ),
  })

  const data = preview.data
  const lastRun = data?.lastRun
  const canPrune = lastRun?.status === 'Exported'

  return (
    <Modal
      open={tenantId !== null}
      onClose={onClose}
      wide
      title={`Archive — ${storeName}`}
      description="Writes old detail out to a file, then lets you delete what it copied."
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {preview.isLoading && <p className="text-sm text-[var(--text-secondary)]">Loading…</p>}

      {error && (
        <p role="alert" className="mb-4 rounded-lg bg-danger-500/10 px-3 py-2 text-sm text-danger-500">
          {error}
        </p>
      )}

      {data && (
        <div className="flex flex-col gap-4">
          <dl className="surface-sunken flex flex-col gap-2 rounded-lg px-4 py-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[var(--text-secondary)]">Keeps</dt>
              <dd className="tabular font-medium">{data.retentionYears} years of detail</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[var(--text-secondary)]">Data begins</dt>
              <dd className="tabular">{data.earliestData ?? 'has never traded'}</dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-[var(--text-secondary)]">Would cover up to</dt>
              <dd className="tabular font-medium">{data.boundary ?? 'nothing is old enough yet'}</dd>
            </div>
          </dl>

          {/* The gate on the whole feature. Archiving a day the rollup never covered would lose it
              outright later, because no summary would be standing behind it. */}
          {data.daysMissingASummary.length > 0 && (
            <div className="rounded-[var(--radius-card)] border border-danger-500/40 bg-danger-500/10 p-4 text-sm">
              <p className="font-medium">
                {data.daysMissingASummary.length} trading day(s) have no daily summary.
              </p>
              <p className="mt-1 text-[var(--text-secondary)]">
                Rebuild the summary history first — the button beside Apply migrations. A day nothing
                summarised would be lost outright, not archived.
              </p>
            </div>
          )}

          {data.counts.length > 0 && (
            <div>
              <p className="mb-2 text-sm font-medium">What would be written</p>
              <ul className="flex flex-col gap-1 text-sm">
                {data.counts.map((row) => (
                  <li key={row.table} className="flex items-center justify-between gap-3">
                    <span className="text-[var(--text-secondary)]">{row.table}</span>
                    <span className="tabular">{row.rows.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Said out loud rather than silently skipped: an operator expecting three years to go and
              seeing less should be told what held the rest back. */}
          {data.ineligible.length > 0 && (
            <div className="text-sm">
              <p className="mb-1 font-medium">Held back</p>
              <ul className="flex flex-col gap-1 text-[var(--text-secondary)]">
                {data.ineligible.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => write.mutate()} disabled={!data.canExport || write.isPending}>
              {write.isPending ? 'Writing…' : 'Write the archive'}
            </Button>
            <span className="text-xs text-[var(--text-secondary)]">
              {data.totalRows.toLocaleString()} rows. Deletes nothing.
            </span>
          </div>

          {data.lastRun && (
            <div className="surface-raised rounded-[var(--radius-card)] border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">Last run</span>
                <Badge tone={data.lastRun.status === 'Exported' ? 'accent' : 'neutral'}>
                  {data.lastRun.status}
                </Badge>
                <span className="tabular text-xs text-[var(--text-secondary)]">
                  through {data.lastRun.boundaryDate} · {formatBytes(data.lastRun.totalBytes)}
                </span>
              </div>

              {data.lastRun.message && (
                <p className="mt-1 text-xs text-[var(--text-secondary)]">{data.lastRun.message}</p>
              )}

              <ul className="mt-3 flex flex-col gap-2">
                {data.lastRun.files.map((name) => (
                  <li key={name} className="flex items-center justify-between gap-3">
                    <span className="tabular truncate text-sm">{name}</span>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        downloadFile(
                          `/api/platform/tenants/${tenantId}/archive/files/${encodeURIComponent(name)}`,
                          name,
                        ).catch(() => setError('Could not download that file.'))
                      }
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </li>
                ))}
              </ul>

              {/* Worth saying where the operator is deciding whether they are covered: a file on the
                  same disk as the database it came from is not a backup. */}
              <p className="mt-3 text-xs text-[var(--text-secondary)]">
                Kept on the server. Download a copy, or make sure the archive folder is covered by
                the nightly backup — a file beside the database it came from is not a backup.
              </p>
            </div>
          )}

          {/* Shut until a verified export exists for this exact cut-off, and asks for the code to be
              typed. Every other confirmation in this panel is one click; this is the one that cannot
              be undone, and an operator can be on the wrong row. */}
          {canPrune && lastRun && (
            <div className="rounded-[var(--radius-card)] border border-danger-500/40 bg-danger-500/10 p-4">
              <p className="text-sm font-medium">Delete what the archive copied</p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Removes every sale, sale line, payment, refund and stock movement up to{' '}
                <span className="tabular font-medium">{lastRun.boundaryDate}</span>, plus empty stock
                batches nothing refers to any more. Daily totals, VAT bands, monthly product figures
                and <span className="font-medium">every void</span> stay — a void records who
                cancelled a sale, which is worth more a year later, not less.
              </p>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                What goes for good: receipt reprints, refunds and per-cashier detail for that period.
              </p>

              {/* Said plainly, or the operator runs it, checks the disk, sees nothing and runs it
                  again. */}
              <p className="mt-2 text-xs text-[var(--text-secondary)]">
                Space is freed inside the database file, not returned to the disk. The file stays the
                size it is and reuses the room from here on.
              </p>

              <div className="mt-3 flex flex-wrap items-end gap-2">
                <Input
                  label={`Type ${data.tenantCode} to confirm`}
                  value={confirmCode}
                  onChange={(event) => setConfirmCode(event.target.value)}
                  autoCapitalize="characters"
                  spellCheck={false}
                  className="tabular tracking-widest uppercase"
                />
                <Button
                  variant="danger"
                  onClick={() => prune.mutate(lastRun.id)}
                  disabled={
                    prune.isPending ||
                    confirmCode.trim().toUpperCase() !== data.tenantCode.toUpperCase()
                  }
                >
                  {prune.isPending ? 'Deleting…' : 'Delete permanently'}
                </Button>
              </div>
            </div>
          )}

          {lastRun?.status === 'Pruned' && (
            <p className="text-sm text-[var(--text-secondary)]">
              This archive has already been used to delete its period. Write a new one to take
              anything more.
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
